import React, { useState, useRef, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { getTabId, getPanelId } from './utils'
import { TabsContainer, TabList, TabItem, TabButton, TabPanel } from './styles'

const TabFilterButtons = ({
  tabs,
  activeTab,
  onTabChange,
  selectedTheme = 'default',
  filter,
  setStatusBgColor,
  t,
  assignmentsCountByFilerName,
  ...props
}) => {
  const [focusedTabIndex, setFocusedTabIndex] = useState(activeTab)
  const tabRefs = useRef([])

  // Update focused tab when active tab changes
  useEffect(() => {
    setFocusedTabIndex(activeTab)
  }, [activeTab])

  const handleKeyDown = useCallback(
    (event, tabIndex) => {
      const keyActions = {
        ArrowLeft: () => (tabIndex > 0 ? tabIndex - 1 : tabs.length - 1),
        ArrowRight: () => (tabIndex < tabs.length - 1 ? tabIndex + 1 : 0),
        Home: () => 0,
        End: () => tabs.length - 1,
        Enter: () => {
          onTabChange(tabIndex)
          return tabIndex
        },
        ' ': () => {
          onTabChange(tabIndex)
          return tabIndex
        },
      }

      const action = keyActions[event.key]
      if (!action) return

      event.preventDefault()
      const newTabIndex = action()
      setFocusedTabIndex(newTabIndex)

      // Focus the target tab
      setTimeout(() => {
        const targetTab = tabRefs.current[newTabIndex]
        targetTab?.focus?.()
      }, 0)
    },
    [tabs.length, onTabChange]
  )

  const handleTabClick = useCallback(
    (tabIndex) => {
      onTabChange(tabIndex)
      setFocusedTabIndex(tabIndex)
    },
    [onTabChange]
  )

  const handleTabFocus = useCallback((tabIndex) => {
    setFocusedTabIndex(tabIndex)
  }, [])

  const setTabRef = useCallback((el, index) => {
    if (el) {
      tabRefs.current[index] = el
    }
  }, [])

  return (
    <TabsContainer {...props}>
      <TabList role="tablist" aria-label="Filter tabs">
        {tabs.map((tab, index) => {
          const isActive = index === activeTab
          const isFocused = index === focusedTabIndex

          return (
            <TabItem key={index} role="presentation">
              <TabButton
                ref={(el) => setTabRef(el, index)}
                role="tab"
                id={getTabId(index)}
                aria-selected={isActive}
                aria-controls={getPanelId(index)}
                tabIndex={isActive ? 0 : -1}
                onClick={() => handleTabClick(index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                onFocus={() => handleTabFocus(index)}
                enabled={isActive}
                selectedTheme={selectedTheme}
                filter={filter}
                setStatusBgColor={setStatusBgColor}
                data-cy={tab.value}
                isFocused={isFocused}
              >
                {assignmentsCountByFilerName[tab.value]}&nbsp;{t(tab.label)}
              </TabButton>
            </TabItem>
          )
        })}
      </TabList>

      {tabs.map((tab, index) => {
        const isActive = index === activeTab

        return (
          <TabPanel
            key={index}
            role="tabpanel"
            id={getPanelId(index)}
            aria-labelledby={getTabId(index)}
            hidden={!isActive}
          >
            {isActive && tab.content}
          </TabPanel>
        )
      })}
    </TabsContainer>
  )
}

TabFilterButtons.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      content: PropTypes.node,
    })
  ).isRequired,
  activeTab: PropTypes.number.isRequired,
  onTabChange: PropTypes.func.isRequired,
  selectedTheme: PropTypes.string,
  filter: PropTypes.string,
  setStatusBgColor: PropTypes.func,
  t: PropTypes.func.isRequired,
  assignmentsCountByFilerName: PropTypes.object.isRequired,
}

TabFilterButtons.defaultProps = {
  selectedTheme: 'default',
  filter: undefined,
  setStatusBgColor: undefined,
}

export default TabFilterButtons
